<template>
  <div class="workflow-stats-page">
    <div class="page-header">
      <h1>Workflow Statistics</h1>
      <button @click="$router.push('/admin/scout/workflow/history')" class="btn-secondary">
        View History
      </button>
    </div>

    <div class="period-selector">
      <button 
        v-for="period in periods" 
        :key="period.days"
        @click="selectPeriod(period.days)"
        :class="['period-btn', { active: selectedDays === period.days }]"
      >
        {{ period.label }}
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading statistics...</p>
    </div>

    <div v-else-if="error" class="error-message">
      <span>⚠️ {{ error }}</span>
      <button @click="loadStats" class="btn-text">Try Again</button>
    </div>

    <div v-else-if="stats" class="stats-content">
      <!-- KPI Cards -->
      <div class="kpi-grid">
        <div class="kpi-card">
          <div class="kpi-icon">📊</div>
          <div class="kpi-content">
            <div class="kpi-label">Total Executions</div>
            <div class="kpi-value">{{ formatNumber(stats.total_executions) }}</div>
            <div class="kpi-subtitle">Last {{ selectedDays }} days</div>
          </div>
        </div>

        <div class="kpi-card success">
          <div class="kpi-icon">✓</div>
          <div class="kpi-content">
            <div class="kpi-label">Success Rate</div>
            <div class="kpi-value">{{ successRate }}%</div>
            <div class="kpi-subtitle">
              {{ stats.status_counts.completed || 0 }} completed
            </div>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon">⏱</div>
          <div class="kpi-content">
            <div class="kpi-label">Average Duration</div>
            <div class="kpi-value">{{ formatDuration(stats.overall_avg_duration) }}</div>
            <div class="kpi-subtitle">Across all executions</div>
          </div>
        </div>

        <div class="kpi-card failure">
          <div class="kpi-icon">✗</div>
          <div class="kpi-content">
            <div class="kpi-label">Failure Rate</div>
            <div class="kpi-value">{{ failureRate }}%</div>
            <div class="kpi-subtitle">
              {{ (stats.status_counts.failed || 0) + (stats.status_counts.timeout || 0) }} failed
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="charts-grid">
        <!-- Status Breakdown -->
        <div class="chart-card">
          <h3>Status Breakdown</h3>
          <div class="status-breakdown">
            <div 
              v-for="(count, status) in stats.status_counts" 
              :key="status"
              class="status-item"
            >
              <div class="status-bar-container">
                <div 
                  class="status-bar"
                  :style="{
                    width: `${(count / stats.total_executions) * 100}%`,
                    backgroundColor: getStatusColor(status)
                  }"
                ></div>
              </div>
              <div class="status-info">
                <StatusBadge :status="status" size="small" />
                <span class="status-count">{{ count }} ({{ getPercentage(count) }}%)</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Duration Comparison -->
        <div class="chart-card">
          <h3>Average Duration by Status</h3>
          <div class="duration-comparison">
            <div 
              v-for="(duration, status) in stats.avg_durations" 
              :key="status"
              class="duration-item"
            >
              <div class="duration-label">
                <StatusBadge :status="status" size="small" />
              </div>
              <div class="duration-bar-container">
                <div 
                  class="duration-bar"
                  :style="{
                    width: `${(duration / maxDuration) * 100}%`,
                    backgroundColor: getStatusColor(status)
                  }"
                >
                  <span class="duration-value">{{ formatDurationShort(duration) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detailed Stats Table -->
      <div class="stats-table-card">
        <h3>Detailed Statistics</h3>
        <table class="stats-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Count</th>
              <th>Percentage</th>
              <th>Average Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(count, status) in stats.status_counts" :key="status">
              <td>
                <StatusBadge :status="status" size="small" />
              </td>
              <td class="stat-value">{{ count }}</td>
              <td class="stat-value">{{ getPercentage(count) }}%</td>
              <td class="stat-value">
                {{ stats.avg_durations[status] ? formatDuration(stats.avg_durations[status]) : 'N/A' }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total</strong></td>
              <td class="stat-value"><strong>{{ stats.total_executions }}</strong></td>
              <td class="stat-value"><strong>100%</strong></td>
              <td class="stat-value"><strong>{{ formatDuration(stats.overall_avg_duration) }}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowHistory from '@/lib/WorkflowHistory';
import StatusBadge from '@/components/workflow/StatusBadge.vue';
import { 
  formatDuration, 
  formatDurationShort, 
  getStatusColor,
  calculateSuccessRate,
  calculateFailureRate
} from '@/shared/workflowHistoryUtils';

export default {
  name: 'WorkflowStats',
  components: {
    StatusBadge
  },
  data() {
    return {
      stats: null,
      loading: false,
      error: null,
      selectedDays: 7,
      periods: [
        { days: 1, label: 'Last 24h' },
        { days: 7, label: '7 Days' },
        { days: 30, label: '30 Days' },
        { days: 90, label: '90 Days' }
      ],
      workflowHistoryService: null
    };
  },
  computed: {
    successRate() {
      if (!this.stats) return 0;
      return calculateSuccessRate(this.stats);
    },
    failureRate() {
      if (!this.stats) return 0;
      return calculateFailureRate(this.stats);
    },
    maxDuration() {
      if (!this.stats || !this.stats.avg_durations) return 1;
      const durations = Object.values(this.stats.avg_durations);
      return Math.max(...durations, 1);
    }
  },
  created() {
    this.workflowHistoryService = new WorkflowHistory(this.$workflowApi);
    this.loadStats();
  },
  methods: {
    formatDuration,
    formatDurationShort,
    getStatusColor,
    
    async loadStats() {
      this.loading = true;
      this.error = null;
      
      try {
        const data = await this.workflowHistoryService.fetchStats(this.selectedDays);
        this.stats = data;
      } catch (err) {
        this.error = err.message || 'Failed to load statistics';
        console.error('Error loading stats:', err);
      } finally {
        this.loading = false;
      }
    },
    
    selectPeriod(days) {
      this.selectedDays = days;
      this.loadStats();
    },
    
    getPercentage(count) {
      if (!this.stats || this.stats.total_executions === 0) return 0;
      return Math.round((count / this.stats.total_executions) * 100 * 100) / 100;
    },
    
    formatNumber(num) {
      return num.toLocaleString();
    }
  }
};
</script>

<style scoped>
.workflow-stats-page {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
}

.btn-secondary {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  background-color: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-default);
}

.btn-secondary:hover {
  background-color: var(--bg-card);
}

.period-selector {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
  padding: 8px;
  background-color: var(--form-bg);
  border-radius: 8px;
  width: fit-content;
  border: 1px solid var(--border-default);
}

.period-btn {
  padding: 8px 20px;
  border: 1px solid transparent;
  border-radius: 6px;
  background-color: transparent;
  color: #9ca3af;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.period-btn:hover {
  background-color: var(--border-default);
  color: var(--text-primary);
}

.period-btn.active {
  background-color: var(--primary-start);
  color: var(--text-primary);
  border-color: var(--primary-start);
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-primary);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  background-color: var(--form-bg);
  border: 1px solid #ef4444;
  color: #fca5a5;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-text {
  background: transparent;
  border: none;
  color: #fca5a5;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.kpi-card {
  background: var(--bg-card);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid var(--border-default);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  border-color: var(--border-hover);
}

.kpi-card.success {
  border-left: 4px solid #10b981;
}

.kpi-card.failure {
  border-left: 4px solid #ef4444;
}

.kpi-icon {
  font-size: 40px;
  opacity: 0.8;
}

.kpi-content {
  flex: 1;
}

.kpi-label {
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  margin-bottom: 4px;
}

.kpi-subtitle {
  font-size: 13px;
  color: #9ca3af;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.chart-card,
.stats-table-card {
  background: var(--bg-card);
  border-radius: 12px;
  padding: 24px;
  border: 1px solid var(--border-default);
}

.chart-card h3,
.stats-table-card h3 {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.status-breakdown,
.duration-comparison {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.status-item,
.duration-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.status-bar-container,
.duration-bar-container {
  background-color: var(--form-bg);
  border-radius: 6px;
  height: 32px;
  overflow: hidden;
  position: relative;
  border: 1px solid var(--border-default);
}

.status-bar,
.duration-bar {
  height: 100%;
  transition: width 0.5s ease;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 12px;
  min-width: 60px;
}

.duration-value {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 13px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}

.status-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-count {
  font-weight: 600;
  color: #9ca3af;
  font-size: 14px;
}

.duration-label {
  margin-bottom: 4px;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
}

.stats-table thead {
  background-color: var(--form-bg);
  border-bottom: 2px solid var(--border-default);
}

.stats-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stats-table tbody tr {
  border-bottom: 1px solid var(--border-default);
}

.stats-table td {
  padding: 16px;
  color: var(--text-primary);
  font-size: 14px;
}

.stats-table tfoot {
  background-color: var(--form-bg);
  border-top: 2px solid var(--border-default);
}

.stats-table tfoot td {
  font-weight: 600;
  color: var(--text-primary);
}

.stat-value {
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  color: var(--text-primary);
}
</style>
